import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { MdsDropdownIconButton } from "@/design-system/components/dropdown";
import { CollectionsViewPageHeaderShareButton } from "@/pages/collections-view/components/CollectionsViewPageHeaderShareButton";
import styled from "@emotion/styled";
import { AnimationTiming } from "@/design-system/constants/common";
import { MdsIconKind } from "@/design-system/components/icon";
import { CollectionsFacetFilter } from "@/components/filters/CollectionsFacetFilter/CollectionsFacetFilter";
import { ContactsFacetFilter } from "@/components/filters/ContactsFacetFilter";
import { NotesListFilter } from "@/modules/filters/notes/NotesListFilter";
import { DateFacetFilter } from "@/components/filters/DateFacetFilter/DateFacetFilter";
import { FadeHorizontalScroll } from "@/components/horizontal-scroll/FadeHorizontalScroll";
import { MediaKindsFacetFilter } from "@/components/filters/MediaKindsFacetFilter/MediaKindsFacetFilter";
import { useTabPageStore } from "@/hooks/useTabPageStore";
import {
  MDSIconButton,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSEditableTitle,
  MDSPanelHeader,
  MDSPanelHeaderSlotName,
  MDSTextVariant,
  MDSIconButtonSize,
  MDSPanelHeaderButtonGroup,
} from "@/mds";
import { mdsPanelWidthsValue } from "@/design-system/foundations/breakpoints";
import { CollectionIcon } from "@/components/collection/CollectionIcon";
import { SortDropdownButton } from "@/components/filters/SortDropdownButton";

export const CollectionsViewPageHeader = observer(() => {
  const page = useTabPageStore("collectionsViewPage");
  const collection = page.collectionObservable;
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const { filters } = page;

  const collectionTitleInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (collection && !collection.label) collectionTitleInputRef.current?.focus();
  }, [collection]);

  useEffect(() => {
    // Make sure to save the collection when the component unmounts because blur is not guaranteed
    // to be called whe navigating away from the page.
    return () => {
      if (collection && !collection.isDeleted) collection.save();
    };
  }, [collection]);

  if (!collection) return null;
  return (
    <Wrapper>
      <MDSPanelHeader>
        <StyledHeaderHeading slot={MDSPanelHeaderSlotName.Heading}>
          <CollectionIcon size={20} collectionId={collection.id} legacy={false} />
          <MDSEditableTitle
            variant={MDSTextVariant.HeaderL}
            placeholder={"Untitled collection"}
            value={collection.title}
            onFocus={() => collection.lock()}
            onChange={e => collection.setTitle(e.currentTarget.value)}
            onBlur={async () => await collection.save()}
            autoFocus={!collection?.title}
            readOnly={!collection.canWrite}
          />
        </StyledHeaderHeading>
        <StyledHeaderSubsection slot={MDSPanelHeaderSlotName.Subsection}>
          <MDSEditableTitle
            variant={MDSTextVariant.ParagraphS}
            value={collection.description}
            placeholder={"Add a description"}
            onFocus={() => collection.lock()}
            onChange={e => collection.setDescription(e.currentTarget.value)}
            onBlur={() => collection.save()}
            autoFocus={collection?.label === ""}
            readOnly={!collection.canWrite}
          />
        </StyledHeaderSubsection>
        <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
          <MDSTooltip
            config={{
              label: "Create note in collection",
              placement: MDSTooltipPlacement.Top,
              delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
            }}
          >
            <MDSIconButton
              iconKind={MdsIconKind.Plus}
              onClick={() => page.handleCreateNewNote()}
              size={MDSIconButtonSize.Medium}
            />
          </MDSTooltip>
          <CollectionsViewPageHeaderShareButton collection={collection} />

          <MDSTooltip
            config={{
              label: collection.isFavorited ? "Unpin from sidebar" : "Pin to sidebar",
              placement: MDSTooltipPlacement.Top,
              delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
            }}
          >
            <MDSIconButton
              iconKind={collection.isFavorited ? MdsIconKind.ThumbtackSolid : MdsIconKind.Thumbtack}
              onClick={() => collection.toggleFavorite()}
              size={MDSIconButtonSize.Medium}
            />
          </MDSTooltip>
          <MDSTooltip
            config={{
              label: "Show filters",
              placement: MDSTooltipPlacement.Top,
              delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
            }}
          >
            <MDSIconButton
              iconKind={MdsIconKind.Filter}
              onClick={() => setShowFilters(!showFilters)}
              isToggled={showFilters}
              size={MDSIconButtonSize.Medium}
            />
          </MDSTooltip>
          <MdsDropdownIconButton
            contentList={page.headerDropdownButtonContentList}
            placement="below-right-alignment"
            tooltipConfig={{
              label: "More",
              placement: MDSTooltipPlacement.Top,
              delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
            }}
            size={MDSIconButtonSize.Medium}
          />
        </MDSPanelHeaderButtonGroup>
      </MDSPanelHeader>
      {showFilters && (
        <WrapperBottom>
          <FiltersContainer>
            <SortDropdownButton
              isToggled={filters.sortBy !== NotesListFilter.DEFAULT_SORT_BY}
              contentList={filters.sortOptions}
              label={filters.sortLabel}
              placement="below-right-alignment"
              dropdownIcon
            />
            {renderSortedFilters(page.filters)}
          </FiltersContainer>
        </WrapperBottom>
      )}
    </Wrapper>
  );
});

const renderSortedFilters = (pageFilters: NotesListFilter) => {
  const filters = [
    {
      isActive: !!pageFilters.params.filter?.date,
      defaultOrder: 2,
      component: (
        <DateFacetFilter
          key="date"
          items={pageFilters.dateFilterItems}
          selectedRange={pageFilters.dateSelectedRange}
          onClear={() => pageFilters.clearDateFilter()}
        />
      ),
    },
    {
      isActive: !!pageFilters.mediaKindsFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 1,
      component: (
        <MediaKindsFacetFilter
          key="media-kinds"
          items={pageFilters.mediaKindsFacetFilterItems}
          onSelectItem={pageFilters.toggleMediaKindItem}
          onSelectClear={pageFilters.clearMediaKinds}
          selectedItems={pageFilters.selectedMediaKinds}
        />
      ),
    },
    {
      isActive: pageFilters.addedBySearchItems.some(item => item.isSelected),
      defaultOrder: 3,
      component: (
        <ContactsFacetFilter
          key="addedBy"
          items={pageFilters.addedBySearchItems}
          searchText={pageFilters.addedBySearchQuery}
          onSearchTextChange={pageFilters.setAddedBySearchQuery}
          onSearchTextClear={pageFilters.clearAddedBySearchQuery}
          onSelectItem={pageFilters.toggleAddedByItem}
          onSelectClear={pageFilters.clearAddedBy}
          buttonPrefix="Added by"
        />
      ),
    },
    {
      isActive: pageFilters.modifiedBySearchItems.some(item => item.isSelected),
      defaultOrder: 4,
      component: (
        <ContactsFacetFilter
          key="modifiedBy"
          items={pageFilters.modifiedBySearchItems}
          searchText={pageFilters.modifiedBySearchQuery}
          onSearchTextChange={pageFilters.setModifiedBySearchQuery}
          onSearchTextClear={pageFilters.clearModifiedBySearchQuery}
          onSelectItem={pageFilters.toggleModifiedByItem}
          onSelectClear={pageFilters.clearModifiedBy}
          buttonPrefix="Edited by"
        />
      ),
    },
    {
      isActive: pageFilters.createdBySearchItems.some(item => item.isSelected),
      defaultOrder: 5,
      component: (
        <ContactsFacetFilter
          key="createdBy"
          items={pageFilters.createdBySearchItems}
          searchText={pageFilters.createdBySearchQuery}
          onSearchTextChange={pageFilters.setCreatedBySearchQuery}
          onSearchTextClear={pageFilters.clearCreatedBySearchQuery}
          onSelectItem={pageFilters.toggleCreatedByItem}
          onSelectClear={pageFilters.clearCreatedBy}
          buttonPrefix="Created by"
        />
      ),
    },
    {
      isActive:
        pageFilters.selectedCollectionTitles.length > 0 ||
        pageFilters.collectionNoCollectionIsSelected,
      defaultOrder: 0,
      component: (
        <CollectionsFacetFilter
          key="collections"
          searchText={pageFilters.collectionsSearchQuery}
          items={pageFilters.collectionsFilterItems}
          onSearchTextChange={pageFilters.setCollectionSearchQuery}
          onSearchTextClear={pageFilters.clearCollectionSearchQuery}
          onSelectItem={pageFilters.toggleCollectionItem}
          onSelectClear={pageFilters.clearCollections}
          buttonPrefix={pageFilters.collectionNoCollectionIsSelected ? "" : "In"}
          buttonLabel="Collections"
          selectedModeKey={pageFilters.selectedCollectionModeKey}
          onSelectMode={pageFilters.onCollectionModeSelectionChange}
          noCollectionIsSelected={pageFilters.collectionNoCollectionIsSelected}
          onNoCollectionIsSelected={pageFilters.setCollectionNoCollectionIsSelected}
          selectedItems={pageFilters.selectedCollectionTitles}
        />
      ),
    },
  ];

  return filters
    .sort((a, b) => {
      const activeSort = Number(b.isActive) - Number(a.isActive);
      if (activeSort !== 0) {
        return activeSort;
      }

      return a.defaultOrder - b.defaultOrder;
    })
    .map(filter => filter.component);
};

const Wrapper = styled.div({});

const WrapperBottom = styled.div(({ theme }) => ({
  padding: theme.spacing.md,
}));

const StyledHeaderHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
`;

const StyledHeaderSubsection = styled.div`
  padding-top: ${({ theme }) => theme.spacing["4"]};
  padding-bottom: ${({ theme }) => theme.spacing["3"]};

  @container mds-panel (max-width: ${mdsPanelWidthsValue.headerBreakpoint}px) {
    padding-top: 0px;
    padding-left: 12px;
  }
`;

const FiltersContainer = styled(FadeHorizontalScroll)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing.sm,
}));
